import React, { useState } from "react";
import { FileText } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import usePrecontoPrinter from "@/lib/usePrecontoPrinter";
import { printReceiptViaQz, thermalPrintError } from "@/lib/printService";

/**
 * Pulsante Preconto: documento NON fiscale sulla termica LAN (Android TCP / QZ sul PC).
 * Mai stampa A4 del browser. Il conto resta aperto.
 */
export default function PrecontoButton({
  mode,
  lines,
  discountPct = 0,
  discountEur = 0,
  total = null,
  tendered = 0,
  notes,
  tenant,
  orderId,
  onOrderSaved,
  testid = "pos-preconto-btn",
  className,
}) {
  const { hasPreconto, precontoPrinter } = usePrecontoPrinter();
  const [busy, setBusy] = useState(false);

  if (!hasPreconto) return null;

  const print = async () => {
    if (!lines?.length) { toast.error("Nessun articolo nel conto"); return; }
    if (!precontoPrinter || precontoPrinter.model === "simulator" || !precontoPrinter.ip) {
      toast.error("Preconto: configura una termica reale (AXON/Epson) con IP di rete. Il simulatore non stampa e non si usa A4.", { duration: 9000 });
      return;
    }
    setBusy(true);
    try {
      let oid = orderId || null;
      const body = { lines, discount_pct: discountPct || 0, discount_eur: discountEur || 0 };
      if (notes) body.notes = notes;
      if (oid) {
        try { await api.patch(`/orders/${oid}`, body); }
        catch { oid = null; }
      }
      if (!oid) {
        const r = await api.post("/orders", { mode, ...body });
        oid = r.data.id;
      }
      onOrderSaved?.(oid);
      const pr = await api.post(`/orders/${oid}/preconto`);
      const o = { ...pr.data.order, receipt_type: "preconto" };
      const t = Number(o.total || total || 0);
      if (tendered && t && tendered >= t - 0.001) {
        o.cash_tendered = tendered;
        o.change_due = Math.round((tendered - t) * 100) / 100;
      }
      const res = await printReceiptViaQz({ tenant, order: o, kind: "preconto" });
      if (res.ok) {
        toast.success(`Preconto su "${res.printer}" — il conto resta aperto`);
      } else {
        toast.error(thermalPrintError(res), { duration: 9000 });
      }
    } catch (e) {
      toast.error(formatApiError(e, "Errore stampa preconto"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      data-testid={testid}
      disabled={busy || !lines?.length}
      onClick={print}
      className={className || "w-full h-11 mb-2 bg-white border-2 border-velta-ink text-velta-ink hover:bg-velta-ink hover:text-white rounded-md text-sm font-medium flex items-center justify-center gap-2 btn-tactile disabled:opacity-40"}
    >
      <FileText className="w-4 h-4" /> Preconto
    </button>
  );
}
